/**
 * Insurance: clinic settings and payers, patient policies (masked for staff), coverage checks, and
 * OPD/IPD claim cases. The clinic is always the active clinic (staff) or the patient's token clinic.
 * Field names mirror the backend records in InsuranceDtos.
 */
import { fetchApi, fetchMultipartApi, fetchPatientApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export type PayerType = 'INSURER' | 'TPA' | 'GOVERNMENT_SCHEME';
export type Relationship = 'SELF' | 'SPOUSE' | 'CHILD' | 'PARENT' | 'OTHER';
export type EligibilityStatus =
  | 'UNKNOWN' | 'PENDING' | 'ELIGIBLE' | 'NOT_ELIGIBLE' | 'PARTIALLY_COVERED' | 'EXPIRED' | 'MANUAL_REVIEW_REQUIRED' | 'ERROR';
export type ClaimType = 'OPD_CASHLESS' | 'OPD_REIMBURSEMENT' | 'IPD_CASHLESS' | 'IPD_REIMBURSEMENT';
export type ClaimStage = 'PREAUTHORIZATION' | 'CLAIM';
export type ClaimStatus = 'DRAFT' | 'SUBMITTED' | 'QUERY' | 'APPROVED' | 'PARTIAL' | 'REJECTED' | 'SETTLED' | 'CANCELLED';
export type DocType =
  | 'CARD_FRONT' | 'CARD_BACK' | 'POLICY_DOCUMENT' | 'ID_PROOF' | 'PREAUTH_FORM' | 'CLAIM_FORM' | 'PAYER_LETTER'
  | 'DISCHARGE_SUMMARY' | 'FINAL_BILL' | 'INVESTIGATION_REPORT' | 'PRESCRIPTION' | 'OTHER';
export type ClaimAction = 'EDIT' | 'SUBMIT' | 'QUERY' | 'ANSWER_QUERY' | 'DECIDE' | 'ENHANCE' | 'START_CLAIM' | 'SETTLE' | 'CANCEL';
export type EstimateHead = 'ROOM' | 'NURSING' | 'INVESTIGATIONS' | 'MEDICINES' | 'CONSUMABLES' | 'PROFESSIONAL_FEES' | 'OT_CHARGES' | 'OTHER';
export type RoomCategory = 'GENERAL' | 'SEMI_PRIVATE' | 'PRIVATE' | 'ICU';
export type ShortfallTreatment = 'WRITE_OFF' | 'RECOVER_FROM_PATIENT';

/** Pre-authorisation request details (standard cashless request form). */
export interface InsPreauthInput {
  treatingDoctorId?: string;
  icdCode?: string;
  expectedStayDays?: number;
  roomCategory?: RoomCategory | '';
  estimate?: Partial<Record<EstimateHead, number>>;
}
export interface InsPreauth {
  treatingDoctorId?: string | null; treatingDoctorName?: string | null; icdCode?: string | null; expectedStayDays?: number | null;
  roomCategory?: RoomCategory | null; estimate: Partial<Record<EstimateHead, number>>; enhancements: number;
}
/** IRDAI turnaround: time with the payer vs the target (pre-auth 60 min, discharge 180 min). */
export interface InsTurnaround { waitingSince: string; waitingMinutes: number; targetMinutes: number; overdue: boolean }
export interface InsReceivables {
  totalDue: number;
  byPayer: Array<{ payerName: string; claims: number; total: number; days0to30: number; days31to60: number; days61to90: number; days90plus: number }>;
  claims: Array<{ claimId: string; claimNumber: string; patientName?: string | null; insurerName?: string | null; tpaName?: string | null;
    claimType: string; approvedAmount: number; approvedAt?: string | null; ageDays: number }>;
  awaitingDecision: number;
  awaitingDecisionAmount: number;
}

export interface InsPayer { id: string; name: string; type: PayerType; clinicOwned: boolean; active: boolean }
export interface InsEmpanelment { payerId: string; payerName: string; payerType: PayerType; cashlessOpd: boolean; cashlessIpd: boolean; active: boolean }
export interface InsSettings { entitled: boolean; enabled: boolean; patientPoliciesEnabled: boolean; empanelments: InsEmpanelment[] }
export interface InsAvailability { offered: boolean; patientPoliciesEnabled: boolean }

export interface InsDocument { id: string; docType: DocType; contentType: string; sizeBytes: number; uploadedByType: string; uploadedAt?: string }
export interface InsPolicy {
  id: string;
  insurerId: string;
  insurerName?: string | null;
  tpaId?: string | null;
  tpaName?: string | null;
  planName?: string | null;
  policyNumberMasked?: string | null;
  /** Only in the patient's own view. */
  policyNumber?: string | null;
  memberId?: string | null;
  policyHolderName?: string | null;
  relationshipToHolder: Relationship;
  validFrom?: string | null;
  validTo?: string | null;
  primary: boolean;
  status: 'ACTIVE' | 'INACTIVE';
  validToday: boolean;
  addedByType: 'PATIENT' | 'STAFF';
  documents: InsDocument[];
}
export interface InsPolicyInput {
  insurerId?: string;
  tpaId?: string;
  planName?: string;
  policyNumber?: string;
  memberId?: string;
  policyHolderName?: string;
  relationshipToHolder?: Relationship;
  validFrom?: string;
  validTo?: string;
  primary?: boolean;
}
export interface InsLink { url: string; expiresInSeconds: number; contentType: string }

export interface InsEligibility {
  id: string;
  patientId: string;
  patientName?: string | null;
  policyId: string;
  insurerName?: string | null;
  tpaName?: string | null;
  policyNumberMasked?: string | null;
  tokenId?: string | null;
  admissionId?: string | null;
  serviceDate: string;
  status: EligibilityStatus;
  provider: string;
  source: 'PATIENT_BOOKING' | 'STAFF';
  requestedAt?: string;
  verificationMethod?: string | null;
  payerReference?: string | null;
  coverageLimitations?: string | null;
  copayPercent?: number | null;
  coveredLimit?: number | null;
  staffNotes?: string | null;
  verifiedByName?: string | null;
  verifiedAt?: string | null;
  message: string;
  disclaimer: string;
  /** Stable code for message (ELIGIBILITY_ + status); absent on older servers. */
  messageKey?: string;
}
export interface InsEligibilityResult {
  status: EligibilityStatus;
  verificationMethod?: string;
  payerReference?: string;
  coverageLimitations?: string;
  copayPercent?: number;
  coveredLimit?: number;
  staffNotes?: string;
}
export interface InsPatientEligibility {
  id: string; serviceDate: string; status: EligibilityStatus; insurerName?: string | null; coverageLimitations?: string | null;
  copayPercent?: number | null; verifiedAt?: string | null; message: string; disclaimer: string; messageKey?: string;
}

export interface InsChecklistItem { id: string; stage: ClaimStage; docType: DocType; required: boolean; status: 'PENDING' | 'RECEIVED' | 'WAIVED'; documentId?: string | null; note?: string | null }
export interface InsEvent { eventType: string; stage: ClaimStage; fromStatus?: ClaimStatus | null; toStatus?: ClaimStatus | null; amount?: number | null; note?: string | null; actorName?: string | null; at: string }
export interface InsReconciliation {
  billId: string; billNumber?: string | null; billTotal: number; insurerShare: number; patientShare: number; patientPaid: number;
  patientDue: number; insurerSettled?: number | null; insurerDue: number; tdsAmount?: number | null; shortfall?: number | null;
  shortfallTreatment?: ShortfallTreatment | null; note: string;
}
export interface InsClaimSummary {
  id: string; claimNumber: string; patientName?: string | null; insurerName?: string | null; claimType: ClaimType; stage: ClaimStage;
  status: ClaimStatus; requestedAmount?: number | null; approvedAmount?: number | null; settledAmount?: number | null; updatedAt?: string;
  turnaround?: InsTurnaround | null;
}
export interface InsClaim {
  id: string; claimNumber: string; patientId: string; patientName?: string | null; policyId: string; insurerName?: string | null;
  tpaName?: string | null; policyNumberMasked?: string | null; claimType: ClaimType; stage: ClaimStage; status: ClaimStatus;
  admissionId?: string | null; tokenId?: string | null; billId?: string | null; eligibilityCheckId?: string | null;
  exchangeProvider: string; payerReference?: string | null; diagnosisSummary?: string | null;
  estimatedAmount?: number | null; preauthApprovedAmount?: number | null; claimedAmount?: number | null; approvedAmount?: number | null;
  patientResponsibility?: number | null; settledAmount?: number | null; settledOn?: string | null; settlementReference?: string | null;
  netReceived?: number | null; tdsAmount?: number | null; shortfallAmount?: number | null; shortfallTreatment?: ShortfallTreatment | null;
  preauth?: InsPreauth | null; turnaround?: InsTurnaround | null;
  submittedAt?: string | null; decidedAt?: string | null; cancelReason?: string | null; createdAt?: string;
  checklist: InsChecklistItem[]; documents: InsDocument[]; events: InsEvent[]; reconciliation?: InsReconciliation | null; actions: ClaimAction[];
}
export interface InsClaimCreate {
  patientId: string; policyId: string; claimType: ClaimType; admissionId?: string; tokenId?: string; billId?: string;
  eligibilityCheckId?: string; estimatedAmount?: number; claimedAmount?: number; diagnosisSummary?: string;
  preauth?: InsPreauthInput;
}
export interface InsClaimUpdate {
  payerReference?: string; estimatedAmount?: number; claimedAmount?: number; billId?: string; diagnosisSummary?: string;
  preauth?: InsPreauthInput;
}
export interface InsPatientContext {
  admissions: Array<{ id: string; admissionNumber?: string | null; admittedOn?: string | null; status?: string | null }>;
  bills: Array<{ id: string; billNumber?: string | null; billDate?: string | null; total?: number | null; category?: string | null; paymentStatus?: string | null; admissionId?: string | null }>;
  appointments: Array<{ id: string; date: string; timeSlot?: string | null; status?: string | null }>;
}
export interface InsPatientClaim {
  claimNumber: string; claimType: ClaimType; stage: ClaimStage; status: ClaimStatus; insurerName?: string | null;
  requestedAmount?: number | null; approvedAmount?: number | null; patientResponsibility?: number | null;
  settledAmount?: number | null; settledOn?: string | null; updatedAt?: string;
}

const post = (body?: unknown): RequestInit => ({ method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const put = (body: unknown): RequestInit => ({ method: 'PUT', body: JSON.stringify(body) });
const enc = encodeURIComponent;
const upload = (docType: DocType, file: File) => {
  const form = new FormData();
  form.append('docType', docType);
  form.append('file', file);
  return form;
};

export const insuranceModule = {
  settings: {
    get: () => fetchApi<InsSettings>('/api/insurance/settings'),
    update: (data: { enabled?: boolean; patientPoliciesEnabled?: boolean }) => fetchApi<InsSettings>('/api/insurance/settings', put(data)),
    empanel: (data: { payerId: string; cashlessOpd?: boolean; cashlessIpd?: boolean; active?: boolean }) =>
      fetchApi<InsSettings>('/api/insurance/settings/empanelments', post(data)),
    addPayer: (name: string, type: PayerType) => fetchApi<InsPayer>('/api/insurance/settings/payers', post({ name, type })),
  },
  payers: () => fetchApi<InsPayer[]>('/api/insurance/payers'),

  policies: (patientId: string) => fetchApi<InsPolicy[]>(`/api/insurance/patients/${enc(patientId)}/policies`),
  addPolicy: (patientId: string, data: InsPolicyInput) => fetchApi<InsPolicy>(`/api/insurance/patients/${enc(patientId)}/policies`, post(data)),
  updatePolicy: (patientId: string, policyId: string, data: InsPolicyInput) =>
    fetchApi<InsPolicy>(`/api/insurance/patients/${enc(patientId)}/policies/${enc(policyId)}`, put(data)),
  deactivatePolicy: (patientId: string, policyId: string) =>
    fetchApi<InsPolicy>(`/api/insurance/patients/${enc(patientId)}/policies/${enc(policyId)}/deactivate`, post()),
  /** Full policy / member number. Audited. */
  reveal: (policyId: string) => fetchApi<{ policyNumber: string; memberId?: string | null }>(`/api/insurance/policies/${enc(policyId)}/reveal`, post()),
  uploadPolicyDocument: (patientId: string, policyId: string, docType: DocType, file: File) =>
    fetchMultipartApi<InsDocument>(`/api/insurance/patients/${enc(patientId)}/policies/${enc(policyId)}/documents`, upload(docType, file)),
  documentLink: (documentId: string) => fetchApi<InsLink>(`/api/insurance/documents/${enc(documentId)}/link`),
  context: (patientId: string) => fetchApi<InsPatientContext>(`/api/insurance/patients/${enc(patientId)}/context`),

  eligibilityQueue: (all = false) => fetchApi<InsEligibility[]>(`/api/insurance/eligibility?all=${all}`),
  patientEligibility: (patientId: string) => fetchApi<InsEligibility[]>(`/api/insurance/patients/${enc(patientId)}/eligibility`),
  /** One page of the coverage-check queue: open checks oldest visit first, or all newest first (medopd-docs 117). */
  eligibilityQueuePage: (all: boolean, page: number, size: number) =>
    fetchApi<PagedResponse<InsEligibility>>(`/api/insurance/eligibility?all=${all}&page=${page}&size=${size}`),
  /** One page of a patient's coverage checks, newest first (medopd-docs 117). */
  patientEligibilityPage: (patientId: string, page: number, size: number) =>
    fetchApi<PagedResponse<InsEligibility>>(`/api/insurance/patients/${enc(patientId)}/eligibility?page=${page}&size=${size}`),
  requestEligibility: (data: { patientId: string; policyId: string; tokenId?: string; admissionId?: string; serviceDate?: string }) =>
    fetchApi<InsEligibility>('/api/insurance/eligibility', post(data)),
  recordEligibility: (checkId: string, data: InsEligibilityResult) =>
    fetchApi<InsEligibility>(`/api/insurance/eligibility/${enc(checkId)}/result`, post(data)),

  claims: (closed = false) => fetchApi<InsClaimSummary[]>(`/api/insurance/claims?closed=${closed}`),
  /** One page of the claim board, most recently updated first (medopd-docs 112). */
  claimsPage: (closed: boolean, page: number, size: number) =>
    fetchApi<PagedResponse<InsClaimSummary>>(`/api/insurance/claims?closed=${closed}&page=${page}&size=${size}`),
  claim: (id: string) => fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}`),
  createClaim: (data: InsClaimCreate) => fetchApi<InsClaim>('/api/insurance/claims', post(data)),
  updateClaim: (id: string, data: InsClaimUpdate) => fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}`, put(data)),
  uploadClaimDocument: (id: string, docType: DocType, file: File) =>
    fetchMultipartApi<InsClaim>(`/api/insurance/claims/${enc(id)}/documents`, upload(docType, file)),
  claimDocumentLink: (id: string, documentId: string) =>
    fetchApi<InsLink>(`/api/insurance/claims/${enc(id)}/documents/${enc(documentId)}/link`),
  checklist: (id: string, itemId: string, status: 'PENDING' | 'RECEIVED' | 'WAIVED', note?: string) =>
    fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/checklist/${enc(itemId)}`, post({ status, note })),
  note: (id: string, note: string) => fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/notes`, post({ note })),
  submit: (id: string, note?: string) => fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/submit`, post({ note })),
  query: (id: string, note: string) => fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/query`, post({ note })),
  answer: (id: string, note: string) => fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/answer`, post({ note })),
  decide: (id: string, data: { status: 'APPROVED' | 'PARTIAL' | 'REJECTED'; amount?: number; note?: string; payerReference?: string }) =>
    fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/decision`, post(data)),
  startClaim: (id: string, data: { billId?: string; claimedAmount?: number }) =>
    fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/start-claim`, post(data)),
  enhance: (id: string, additionalAmount: number, reason: string) =>
    fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/enhance`, post({ additionalAmount, reason })),
  receivables: () => fetchApi<InsReceivables>('/api/insurance/receivables'),
  settle: (id: string, data: { netReceived: number; tdsAmount?: number; settledOn: string; reference: string; shortfallTreatment?: ShortfallTreatment; note?: string }) =>
    fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/settlement`, post(data)),
  cancel: (id: string, note: string) => fetchApi<InsClaim>(`/api/insurance/claims/${enc(id)}/cancel`, post({ note })),
};

/** Patient portal calls (patient token; dependant-aware). */
export const patientInsuranceModule = {
  availability: () => fetchPatientApi<InsAvailability>('/api/patient/insurance/availability'),
  payers: () => fetchPatientApi<InsPayer[]>('/api/patient/insurance/payers'),
  policies: () => fetchPatientApi<InsPolicy[]>('/api/patient/insurance/policies'),
  addPolicy: (data: InsPolicyInput) => fetchPatientApi<InsPolicy>('/api/patient/insurance/policies', post(data)),
  updatePolicy: (id: string, data: InsPolicyInput) => fetchPatientApi<InsPolicy>(`/api/patient/insurance/policies/${enc(id)}`, put(data)),
  deactivatePolicy: (id: string) => fetchPatientApi<InsPolicy>(`/api/patient/insurance/policies/${enc(id)}/deactivate`, post()),
  uploadDocument: (policyId: string, docType: DocType, file: File) =>
    fetchPatientApi<InsDocument>(`/api/patient/insurance/policies/${enc(policyId)}/documents`, { method: 'POST', body: upload(docType, file) }),
  documentLink: (documentId: string) => fetchPatientApi<InsLink>(`/api/patient/insurance/documents/${enc(documentId)}/link`),
  removeDocument: (documentId: string) => fetchPatientApi<boolean>(`/api/patient/insurance/documents/${enc(documentId)}`, { method: 'DELETE' }),
  eligibility: () => fetchPatientApi<InsPatientEligibility[]>('/api/patient/insurance/eligibility'),
  claims: () => fetchPatientApi<InsPatientClaim[]>('/api/patient/insurance/claims'),
  /** One page of coverage checks, newest first; the unpaged call stops at 50 (medopd-docs 117). */
  eligibilityPage: (page: number, size: number) =>
    fetchPatientApi<PagedResponse<InsPatientEligibility>>(`/api/patient/insurance/eligibility?page=${page}&size=${size}`),
  /** One page of claims, newest first (medopd-docs 117). */
  claimsPage: (page: number, size: number) =>
    fetchPatientApi<PagedResponse<InsPatientClaim>>(`/api/patient/insurance/claims?page=${page}&size=${size}`),
};
